import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Snackbar,
    Alert
} from '@mui/material';
import { useFeeScheduleForm } from './hooks';
import {
    DescriptionField,
    FeeTypeSelects,
    DateFields,
    CategoryField,
    DialogActionButtons
} from './components';

interface AddEditFeeScheduleDialogProps {
    feeType: 'CPT' | 'DRG';
}

const AddEditFeeScheduleDialog: React.FC<AddEditFeeScheduleDialogProps> = ({ feeType }) => {
    const [open, setOpen] = useState(false);

    const {
        formData,
        formErrors,
        isLoading,
        navigationError,
        handleFormChange,
        handleSave,
        clearNavigationError
    } = useFeeScheduleForm({ feeType, open });

    const handleClose = () => {
        setOpen(false);
    };

    const handleSaveAndClose = async () => {
        await handleSave();
        if (!formErrors.submit) {
            handleClose();
        }
    };

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                onClick={() => setOpen(true)}
                size="small"
            >
                Create {feeType} Fee Schedule
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Create {feeType} Fee Schedule
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <DescriptionField
                            description={formData.description}
                            error={formErrors.description}
                            onChange={(value) => handleFormChange('description', value)}
                        />

                        <FeeTypeSelects
                            feeType={formData.feeType}
                            feeSubType={formData.feeSubType}
                            feeTypeError={formErrors.feeType}
                            feeSubTypeError={formErrors.feeSubType}
                            onFeeTypeChange={(value) => handleFormChange('feeType', value)}
                            onFeeSubTypeChange={(value) => handleFormChange('feeSubType', value)}
                        />

                        <CategoryField
                            category={formData.category}
                            error={formErrors.category}
                            onChange={(value) => handleFormChange('category', value)}
                        />

                        <DateFields
                            effectiveDate={formData.effectiveDate}
                            endDate={formData.endDate}
                            effectiveDateError={formErrors.effectiveDate}
                            endDateError={formErrors.endDate}
                            onEffectiveDateChange={(value) => handleFormChange('effectiveDate', value)}
                            onEndDateChange={(value) => handleFormChange('endDate', value)}
                        />

                        {formErrors.submit && (
                            <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                                {formErrors.submit}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>

                <DialogActionButtons
                    isLoading={isLoading}
                    onCancel={handleClose}
                    onSave={handleSaveAndClose}
                />
            </Dialog>

            <Snackbar
                open={!!navigationError}
                autoHideDuration={6000}
                onClose={clearNavigationError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={clearNavigationError} severity="warning" sx={{ width: '100%' }}>
                    {navigationError}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddEditFeeScheduleDialog;
